import { useGetList, useTimeout } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";

import type { Contact, ContactNote } from "../types";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardStepper } from "./DashboardStepper";
import { Welcome } from "./Welcome";
import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { useConfigurationContext } from "../root/ConfigurationContext";

/**
 * The generic mobile app-bar (logo + title + refresh button): shared by
 * `MobileDashboard` (below) and `AdvisorToday.tsx`'s mobile rendering, so
 * an asesora's "Hoy" home gets the same chrome (and the same
 * `MobileRefreshButton`, inside `MobileHeader`) as every other role's
 * mobile dashboard.
 */
export const MobileHomeHeader = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  return (
    <MobileHeader>
      <div className="flex min-w-0 items-center gap-2 text-secondary-foreground no-underline py-3">
        <img
          className="[.light_&]:hidden h-6 shrink-0"
          src={darkModeLogo}
          alt={title}
        />
        <img
          className="[.dark_&]:hidden h-6 shrink-0"
          src={lightModeLogo}
          alt={title}
        />
        {/* Guide section 6/8: no horizontal overflow at 390px, even for a
            long app title configured in Settings. */}
        <h1 className="text-xl font-semibold truncate">{title}</h1>
      </div>
    </MobileHeader>
  );
};

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <>
    <MobileHomeHeader />
    <MobileContent>{children}</MobileContent>
  </>
);

const Loading = () => (
  <Wrapper>
    <Skeleton className="h-4 w-3/4 mb-4" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-full mb-2" />
  </Wrapper>
);

export const MobileDashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });
  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });
  const oneSecondHasPassed = useTimeout(1000);

  const isPending = isPendingContact || isPendingContactNotes;

  if (isPending) {
    return oneSecondHasPassed ? <Loading /> : null;
  }

  if (!totalContact) {
    return (
      <Wrapper>
        <DashboardStepper step={1} />
      </Wrapper>
    );
  }

  if (!totalContactNotes) {
    return (
      <Wrapper>
        <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {/* MobileDashboard only renders below the md breakpoint (see CRM.tsx),
          so a single-column flex layout is enough here -- no md: grid
          needed, unlike the desktop Dashboard. */}
      <div className="flex flex-col gap-6 mt-1">
        {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
        <DashboardActivityLog />
      </div>
    </Wrapper>
  );
};
